/**
 * In-app browser (webview) detection and escape-to-real-browser helpers.
 *
 * @origin: v2 | @since: 8100552a | @used-by: ALL | @purpose: detect embedded
 * webviews (PetPooja/Instagram/etc.) and offer customers a way to escape to
 * a real browser before login, since camera/location/OTP flows are
 * unreliable inside them. Ported verbatim from v2 (isolated utility with no
 * v2-specific coupling); see OpenInBrowserBanner.tsx for the UI that uses it.
 *
 * WHY THIS EXISTS
 * ===============
 * BAP partners such as PetPooja link to our journey from inside their own
 * native app, which opens the URL in an embedded webview rather than the
 * device browser. Embedded webviews are a poor host for this journey:
 *
 *   * They frequently block or silo third-party cookies, and some clear
 *     storage when the host app is backgrounded — which can break the session
 *     mid-journey.
 *   * Camera/microphone/geolocation permissions behave inconsistently, and the
 *     journey depends on all three (selfie, shop video, location capture).
 *   * There is no address bar, so the customer cannot verify they are on our
 *     real domain before typing an OTP — a phishing-hygiene concern.
 *
 * So we offer the customer a way out, at the START of the journey.
 *
 * WHY ONLY AT THE START
 * ---------------------
 * Cookies and sessionStorage do NOT transfer from a webview to the real
 * browser. Escaping after login would silently drop the session and dump the
 * user on a logged-out page. The banner therefore belongs on the pre-login
 * entry page, where there is no state to lose.
 *
 * =============================================================================
 * PLATFORM REALITY — read before changing any of this
 * =============================================================================
 *
 * ANDROID: solvable. An `intent://` URL with `category=android.intent.category.BROWSABLE`
 * and NO `package=` component asks the OS to resolve the link itself. Because
 * several apps can handle an http(s) VIEW intent, Android shows the system
 * "Open with" chooser (or goes straight to the default browser if the user has
 * set one). Adding `package=com.android.chrome` would instead force Chrome and
 * fail outright on devices without it — which is why the package is
 * deliberately omitted.
 *
 * iOS: NOT solvable programmatically. There is no public API, URL scheme or JS
 * hook that lets web content escape a UIWebView/WKWebView into Safari, and
 * `googlechrome://` only works if Chrome is installed and merely swaps one app
 * for another. Apple provides no chooser. The only reliable path is to tell the
 * user to use the host app's own "Open in Safari" affordance, so on iOS this
 * module intentionally offers copy-to-clipboard plus instructions rather than
 * pretending a redirect will work.
 *
 * Consequently `attemptOpenInDeviceBrowser()` reports whether it *could* try,
 * and callers must always keep a manual fallback visible.
 */

/** Where the current device sits, as far as escaping a webview is concerned. */
export type MobilePlatform = 'android' | 'ios' | 'other';

const ua = (): string =>
    typeof navigator === 'undefined' ? '' : navigator.userAgent || '';

export const getMobilePlatform = (): MobilePlatform => {
    const s = ua();
    if (/android/i.test(s)) return 'android';
    // iPadOS 13+ reports a desktop-Safari UA, so the touch-point check is what
    // actually catches modern iPads.
    if (/iphone|ipad|ipod/i.test(s)) return 'ios';
    if (
        /macintosh/i.test(s) &&
        typeof navigator !== 'undefined' &&
        (navigator.maxTouchPoints ?? 0) > 1
    ) {
        return 'ios';
    }
    return 'other';
};

/**
 * Known in-app browser markers.
 *
 * Grouped and commented because a bare regex here would be unmaintainable —
 * and because a FALSE POSITIVE is costly: it would show an "open in browser"
 * banner to someone already in Chrome, which looks broken and could cost a
 * conversion. Every token below is therefore specific to a host app, never a
 * generic word like "mobile".
 */
const WEBVIEW_MARKERS: RegExp[] = [
    // Meta family. FBAN/FBAV = Facebook app, FB_IAB = its in-app browser,
    // Instagram ships its own token.
    /\bFBAN\b|\bFBAV\b|\bFB_IAB\b|\bFBIOS\b/i,
    /\bInstagram\b/i,
    // Messaging apps that open links internally.
    /\bLine\/|\bMicroMessenger\b|\bWhatsApp\b|\bTelegram\b/i,
    // Snapchat / Pinterest / TikTok.
    /\bSnapchat\b|\bPinterest\b|\bBytedanceWebview\b|\bmusical_ly\b|\bTikTok\b/i,
    // Twitter/X and LinkedIn.
    /\bTwitter\b|\bLinkedInApp\b/i,
    // Generic Android WebView marker. Android's WebView appends "; wv" to the
    // UA, which normal Chrome for Android never does — this is the token that
    // catches unbranded partner apps like PetPooja's.
    /;\s*wv\)/i,
    // Some Android webviews expose a bare "Version/x.y Chrome/..." pairing.
    /\bVersion\/[\d.]+\s+Chrome\//i,
];

/** Detects an iOS WKWebView that is NOT Safari and NOT a real browser app. */
const isIosStandaloneWebview = (): boolean => {
    const s = ua();
    if (!/iphone|ipad|ipod/i.test(s)) return false;
    // Real iOS browsers all advertise themselves: Safari has "Safari",
    // Chrome "CriOS", Firefox "FxiOS", Edge "EdgiOS", Opera "OPiOS".
    const isKnownBrowser = /Safari|CriOS|FxiOS|EdgiOS|OPiOS/i.test(s);
    // An embedded WKWebView renders with AppleWebKit but omits "Safari".
    return /AppleWebKit/i.test(s) && !isKnownBrowser;
};

/**
 * True when the page is running inside a host app's embedded browser.
 *
 * Errs towards FALSE. Being wrong in the "not a webview" direction just means
 * we show no banner and the journey proceeds normally in a slightly worse
 * container; being wrong the other way puts a confusing banner in front of a
 * perfectly good browser.
 */
export const isInAppBrowser = (): boolean => {
    const s = ua();
    if (!s) return false;
    if (WEBVIEW_MARKERS.some((re) => re.test(s))) return true;
    if (isIosStandaloneWebview()) return true;
    return false;
};

/**
 * Builds the Android `intent://` URL that triggers the system browser chooser.
 *
 * Shape:
 *   intent://<host><path><query>#Intent;scheme=https;action=...VIEW;
 *   category=...BROWSABLE;S.browser_fallback_url=<encoded original>;end
 *
 * `S.browser_fallback_url` is what the webview loads if it refuses to hand the
 * intent to the OS, so a failed escape degrades to "stay exactly where you
 * were" instead of a blank page.
 *
 * NOTE the scheme is stripped from the intent body and declared separately via
 * `scheme=`; leaving "https://" in the body produces a malformed intent that
 * silently does nothing.
 */
export const buildAndroidIntentUrl = (absoluteUrl: string): string => {
    const withoutScheme = absoluteUrl.replace(/^https?:\/\//i, '');
    const scheme = absoluteUrl.toLowerCase().startsWith('http://') ? 'http' : 'https';

    return [
        `intent://${withoutScheme}#Intent`,
        `scheme=${scheme}`,
        'action=android.intent.action.VIEW',
        'category=android.intent.category.BROWSABLE',
        `S.browser_fallback_url=${encodeURIComponent(absoluteUrl)}`,
        'end',
    ].join(';');
};

/**
 * The current page's fully-qualified URL, query string included.
 *
 * The query string matters: `?bap=PetPooja` is what attributes the lead to the
 * partner, so dropping it would silently break attribution for every user who
 * escapes the webview.
 */
export const getCurrentAbsoluteUrl = (): string =>
    typeof window === 'undefined' ? '' : window.location.href;

/**
 * Asks the OS to reopen `absoluteUrl` in a real browser.
 *
 * Returns true only when a mechanism was actually invoked (Android). Returns
 * false on iOS and elsewhere, where the caller must fall back to instructing
 * the user — see the iOS note in the module docstring.
 *
 * Being invoked is NOT the same as succeeding: a webview may swallow the
 * intent, which is why `browser_fallback_url` is set and why the calling UI
 * keeps its manual instructions on screen afterwards.
 */
export const attemptOpenInDeviceBrowser = (absoluteUrl: string): boolean => {
    if (getMobilePlatform() !== 'android') return false;

    try {
        // Assigning to location (rather than window.open) is deliberate:
        // webviews commonly block popups, and an intent:// popup is a popup.
        window.location.href = buildAndroidIntentUrl(absoluteUrl);
        return true;
    } catch {
        return false;
    }
};

/**
 * Clipboard copy with a fallback for the many webviews that expose no
 * `navigator.clipboard` (it requires a secure context and is often absent in
 * embedded browsers). The legacy `execCommand('copy')` path is the only thing
 * that works there, so it is kept on purpose.
 */
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // fall through to the legacy path
    }

    try {
        const el = document.createElement('textarea');
        el.value = text;
        // Keep it off-screen and non-focusable-looking, but still selectable —
        // display:none or visibility:hidden would make the copy fail.
        el.setAttribute('readonly', '');
        el.style.position = 'fixed';
        el.style.top = '-1000px';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        el.setSelectionRange(0, text.length); // iOS needs the explicit range
        const ok = document.execCommand('copy');
        document.body.removeChild(el);
        return ok;
    } catch {
        return false;
    }
};
